import './Onboarding.scss'

import { useConstant, useFn } from '@eturi/react'
import { useT } from '@eturi/react-i18next'
import type { OnboardingUser } from '@op/services'
import {
	addOnboardingUser,
	getSKUForPlatformTier,
	onboardingUsers$,
	removeOnboardingUser,
	skuDefs$,
} from '@op/services'
import { createSelector } from '@reduxjs/toolkit'
import type { FormApi } from 'final-form'
import { useMemo } from 'react'
import { Form } from 'react-final-form'
import { useSelector } from 'react-redux'
import { disabledProps } from '../../../common'
import { generateNewAccountDevice } from '../../../thunks/device'
import { useAppDispatch } from '../../../types'
import { ActionBtn, Btn } from '../../../widgets'
import type { ChildNameUser } from '../../Fields/ChildNameField'
import { ChildNameField } from '../../Fields/ChildNameField'
import { SelectField } from '../../Fields/SelectField'
import { useChildAgeField } from '../../Fields/useChildAgeField'
import { ChildProfilePreview } from '../Shared/ChildPreview/ChildProfilePreview'
import { OnboardingScreen } from './OnboardingScreen'

type AddChildFormData = {
	readonly birthday: string
	readonly name: string
}

export const OnboardingChildrenScreen = () => {
	const [t] = useT()
	const d = useAppDispatch()

	const childAgeField = useChildAgeField(null, false, true)
	const onboardingUsers = useSelector(onboardingUsers$)
	const hasMaxOnboardingUsers$ = useConstant(() =>
		createSelector(onboardingUsers$, skuDefs$, (users, defs) => {
			const premDef = getSKUForPlatformTier(defs, 'premium', 'stripe')
			const maxUsers = premDef?.child_users_per_account ?? 20

			return users.length >= maxUsers
		}),
	)
	const hasMaxOnboardingUsers = useSelector(hasMaxOnboardingUsers$)
	const onboardingChildNameUsers = useMemo(
		() => onboardingUsers.map((u): ChildNameUser => ({ name: u.name })),
		[onboardingUsers],
	)

	const INITIAL_VALUES = useConstant(
		(): AddChildFormData => ({
			birthday: childAgeField.props.initialValue,
			name: '',
		}),
	)

	const handleRemoveUser = useFn((user: OnboardingUser) => {
		d(removeOnboardingUser(user))
	})

	const handleSubmit = useFn((v: AddChildFormData, f: FormApi<AddChildFormData>) => {
		const device = d(generateNewAccountDevice())
		d(
			addOnboardingUser({
				age: childAgeField.birthdayToAge(v.birthday),
				name: v.name.trim(),
				time_zone: device.time_zone,
			}),
		)
		setTimeout(() => {
			f.initialize(INITIAL_VALUES)
			f.reset()
		}, 0)
	})

	return (
		<OnboardingScreen>
			<OnboardingScreen.Header>
				Next, tell us about your children to help us tailor their settings.
			</OnboardingScreen.Header>

			<OnboardingScreen.Content className="gap-3">
				{onboardingUsers.length > 0 && (
					<div className="w-full flex flex-col gap-4">
						{onboardingUsers.map((user, idx) => (
							<div
								key={user.name}
								className="onboarding-shadow bg-white w-full flex items-center justify-between p-3 sm:p-4 rounded-2xl sm:rounded-3xl"
							>
								<ChildProfilePreview idx={idx} age={`Age ${user.age}`} name={user.name} />
								<ActionBtn icon="x" size="sm" invert onClick={() => handleRemoveUser(user)} />
							</div>
						))}
					</div>
				)}

				{onboardingUsers.length > 0 && <hr className="w-full h-px" />}

				{!hasMaxOnboardingUsers && (
					<div className="flex flex-col gap-3 w-full">
						<Form
							initialValues={INITIAL_VALUES}
							onSubmit={handleSubmit}
							render={(fp) => (
								<form
									className="w-full gap-2"
									{...disabledProps(fp.submitting)}
									onSubmit={fp.handleSubmit}
									noValidate
								>
									<ChildNameField className="!bg-transparent" users={onboardingChildNameUsers} />

									<SelectField {...childAgeField.props} className="!bg-transparent" />

									<Btn
										className="!text-teal border-teal border-solid w-fit m-auto !min-w-40"
										invert
										onClick={fp.handleSubmit}
										disabled={fp.submitting}
									>
										{t('actions.add')}
									</Btn>
								</form>
							)}
						/>
					</div>
				)}
			</OnboardingScreen.Content>
		</OnboardingScreen>
	)
}
